import { ReactNode } from 'react'
import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ConfirmationDialog from '../../../../components/layout/ConfirmationDialog'
import AuditLogDrawer from '../../../audit/AuditLogDrawer'
import { useImpersonation } from '../../../../io/context/ImpersonationContext'

type PageLayoutProps = {
  title: string
  subtitle?: string
  children: ReactNode
  actions?: ReactNode
  auditTargetId?: string
  showBack?: boolean
  showCancel?: boolean
  showSave?: boolean
  showDelete?: boolean
  saveDisabled?: boolean
  submitting?: boolean
  onSave?: () => void
  onDelete?: () => void
}

const PageLayout = ({
  title,
  subtitle,
  children,
  actions,
  auditTargetId,
  showBack,
  showCancel,
  showSave,
  showDelete,
  saveDisabled,
  submitting,
  onSave,
  onDelete,
}: PageLayoutProps) => {
  const navigate = useNavigate()
  const { isReadOnly } = useImpersonation()
  const writesAllowed = !isReadOnly

  return (
    <Stack padding={3} spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Stack>
          <Typography variant="h4" data-testid="entity-page-title">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="subtitle1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Stack>
        {(actions || auditTargetId) && (
          <Stack direction="row" sx={{ alignItems: 'center' }} data-testid="entity-page-actions">
            {auditTargetId && <AuditLogDrawer targetId={auditTargetId} />}
            {actions}
          </Stack>
        )}
      </Stack>
      <Stack sx={{ flex: 1, minHeight: 0 }}>{children}</Stack>
      <Stack direction="row" spacing={3} sx={{ alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0 }}>
        {showBack && (
          <Button onClick={() => navigate(-1)} data-testid="entity-action-back">
            Back
          </Button>
        )}
        {showCancel && (
          <Button onClick={() => navigate(-1)} disabled={submitting} data-testid="entity-action-cancel">
            Cancel
          </Button>
        )}
        {showDelete && onDelete && writesAllowed && (
          <ConfirmationDialog
            title={`There may still be references to this entity in the system. Are you sure you want to
                      delete this entity? This action cannot be undone.`}
            content={<Typography>Delete</Typography>}
            onConfirm={onDelete}
            triggerTestId="entity-action-delete"
            cancelTestId="entity-action-delete-cancel"
            confirmTestId="entity-action-delete-confirm"
            dialogTestId="entity-action-delete-dialog"
            disabled={submitting}
          />
        )}
        {showSave && onSave && writesAllowed && (
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saveDisabled || submitting}
            startIcon={submitting ? <CircularProgress size="1em" color="inherit" /> : undefined}
            data-testid="entity-action-save"
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        )}
      </Stack>
    </Stack>
  )
}

export default PageLayout
